import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';

export const Route = createFileRoute('/account/email/confirm')({
  component: ConfirmEmailChangePage,
  validateSearch: (search: Record<string, unknown>) => ({
    userId: typeof search.userId === 'string' ? search.userId : '',
    email: typeof search.email === 'string' ? search.email : '',
    token: typeof search.token === 'string' ? search.token : '',
  }),
});

function ConfirmEmailChangePage() {
  const { userId, email, token } = Route.useSearch();
  const confirm = useMutation({
    mutationFn: () => api('/account/change-email-confirm', { method: 'POST', json: { userId, email, token } }),
  });

  useEffect(() => {
    if (userId && email && token) confirm.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, email, token]);

  return (
    <div className="space-y-6">
      <div>
        <div className="section-ref">Email change</div>
        <h2 className="mt-1.5 font-serif text-2xl font-medium tracking-tight">Confirm email change</h2>
        <div className="mt-1 gov-rule" aria-hidden />
      </div>
      <Card>
        <CardContent className="p-7">
          {!userId || !email || !token ? (
            <Status tone="danger">
              <AlertTriangle className="size-4" />
              Missing parameters in the confirmation link. Try requesting another change-email
              link from your account page.
            </Status>
          ) : confirm.isPending ? (
            <Status tone="navy">
              <Loader2 className="size-4 animate-spin" />
              Confirming…
            </Status>
          ) : confirm.isSuccess ? (
            <Status tone="success">
              <CheckCircle2 className="size-4" />
              Email changed to <span className="font-mono">{email}</span>.
            </Status>
          ) : confirm.isError ? (
            <Status tone="danger">
              <AlertTriangle className="size-4" />
              Confirmation failed. The link may have expired — request another from your account.
            </Status>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function Status({
  tone,
  children,
}: {
  tone: 'navy' | 'success' | 'danger';
  children: React.ReactNode;
}) {
  const map = {
    navy: 'border-(--color-navy) bg-(--color-navy-soft) text-(--color-navy)',
    success: 'border-(--color-forest) bg-(--color-forest-soft) text-(--color-forest)',
    danger: 'border-(--color-maroon) bg-(--color-maroon-soft) text-(--color-maroon)',
  } as const;
  return (
    <p
      className={`flex items-start gap-2.5 rounded-(--radius-sm) border-l-[3px] px-3 py-2.5 text-[14px] font-medium ${map[tone]}`}
    >
      {children}
    </p>
  );
}
